import React, { useState } from 'react';
import { Box, Typography, Switch, Tooltip } from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { AutoAwesome as AIIcon } from '@mui/icons-material';
import { useMIA } from '../../../context/MIAContext';
import { useTheme } from '../../../design-system/providers/ThemeProvider';
import MIAActivationDialog from '../../organisms/Dialogs/MIAActivationDialog';

/**
 * MIAToggle - Toggle para ativar/desativar Mensure IA (MIA)
 *
 * Componente atômico que exibe um switch estilizado para controlar
 * todas as funcionalidades de IA do sistema.
 * Requer token de ativação fornecido pelo time Mensure.
 */
const MIAToggle = ({ compact = false }) => {
  const muiTheme = useMuiTheme();
  const { miaColors } = useTheme();
  const { enabled, activated, toggle, deactivate } = useMIA();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Tooltip com descrição completa das funcionalidades
  const tooltipContent = (
    <Box sx={{ p: 1, maxWidth: 280 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: miaColors.primary }}>
        Mensure IA (MIA)
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.9, fontSize: '0.8rem' }}>
        Pacote de inteligência artificial para análise avançada de mídia:
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2, '& li': { mb: 0.5, fontSize: '0.75rem' } }}>
        <li><strong>Análise de Sentimento</strong> - Detecta automaticamente se a matéria é positiva, negativa ou neutra</li>
        <li><strong>Propagação de Sentimento</strong> - Aplica sentimento para artigos similares</li>
        <li><strong>Tags Automáticas</strong> - Sugere tags relevantes baseadas no conteúdo</li>
        <li><strong>Detecção de Similaridade</strong> - Identifica matérias relacionadas ou duplicadas</li>
        <li><strong>Scraping Inteligente</strong> - Extração otimizada de conteúdo</li>
        <li><strong>Resumo Automático</strong> - Gera sínteses do conteúdo das matérias</li>
      </Box>
      {!activated && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'warning.main', fontStyle: 'italic' }}>
          Requer token de ativação. Clique para ativar.
        </Typography>
      )}
      {activated && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'success.main' }}>
          {enabled ? 'Ativo - Clique para desativar temporariamente' : 'Pausado - Clique para reativar'}
        </Typography>
      )}
    </Box>
  );

  const handleClick = () => {
    if (!activated) {
      // Se não está ativado, abrir dialog de ativação
      setDialogOpen(true);
    } else {
      // Se já está ativado, toggle entre ativo/pausado
      toggle();
    }
  };

  const handleDeactivate = () => {
    deactivate();
  };

  return (
    <>
      <Tooltip
        title={tooltipContent}
        arrow
        placement="bottom-end"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: muiTheme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
              color: 'text.primary',
              boxShadow: 3,
              '& .MuiTooltip-arrow': {
                color: muiTheme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
              }
            }
          }
        }}
      >
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.4, sm: 0.75 },
            cursor: 'pointer',
            px: { xs: 0.75, sm: 1.5 },
            py: { xs: 0.2, sm: 0.5 },
            borderRadius: 2,
            backgroundColor: enabled && activated
              ? muiTheme.palette.mode === 'dark'
                ? `${miaColors.primary}26`  // 15% opacity
                : `${miaColors.primary}1A`  // 10% opacity
              : 'transparent',
            border: '1px solid',
            borderColor: enabled && activated
              ? muiTheme.palette.mode === 'dark'
                ? `${miaColors.primary}66`  // 40% opacity
                : `${miaColors.primary}4D`  // 30% opacity
              : !activated
                ? muiTheme.palette.mode === 'dark'
                  ? `${miaColors.inactive}66`  // 40% opacity
                  : `${miaColors.inactive}4D`  // 30% opacity
                : 'divider',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: enabled && activated
                ? muiTheme.palette.mode === 'dark'
                  ? `${miaColors.primary}40`  // 25% opacity
                  : `${miaColors.primary}26`  // 15% opacity
                : muiTheme.palette.action.hover,
              borderColor: enabled && activated
                ? muiTheme.palette.mode === 'dark'
                  ? `${miaColors.primary}99`  // 60% opacity
                  : `${miaColors.primary}80`  // 50% opacity
                : muiTheme.palette.divider
            }
          }}
        >
          <AIIcon
            sx={{
              fontSize: { xs: 14, sm: 18 },
              color: !activated
                ? miaColors.inactive
                : enabled
                  ? miaColors.primary
                  : 'text.disabled',
              transition: 'color 0.2s ease'
            }}
          />
          {!compact && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.65rem', sm: '0.8rem' },
                color: !activated
                  ? miaColors.inactive
                  : enabled
                    ? miaColors.primary
                    : 'text.secondary',
                letterSpacing: '0.5px',
                transition: 'color 0.2s ease',
                userSelect: 'none'
              }}
            >
              MIA
            </Typography>
          )}
          <Switch
            checked={enabled && activated}
            disabled={!activated}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (activated) toggle();
            }}
            sx={{
              width: { xs: 28, sm: 36 },
              height: { xs: 16, sm: 20 },
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: { xs: '1px', sm: '2px' },
                transitionDuration: '200ms',
                '&.Mui-checked': {
                  transform: { xs: 'translateX(12px)', sm: 'translateX(16px)' },
                  color: '#fff',
                  '& + .MuiSwitch-track': {
                    backgroundColor: miaColors.primary,
                    opacity: 1,
                    border: 0
                  }
                },
                '&.Mui-disabled': {
                  '& + .MuiSwitch-track': {
                    opacity: 0.5
                  }
                }
              },
              '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: { xs: 14, sm: 16 },
                height: { xs: 14, sm: 16 }
              },
              '& .MuiSwitch-track': {
                borderRadius: 10,
                backgroundColor: !activated
                  ? miaColors.inactive
                  : muiTheme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
                opacity: !activated ? 0.5 : 1
              }
            }}
          />
        </Box>
      </Tooltip>

      <MIAActivationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default MIAToggle;
